import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  ImmobilieUebernehmenPayload,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import { SagaIterator } from 'redux-saga';
import { call, cancel, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  App,
  BasisAdresse,
  isBasisAdresseExist,
  mapVorbelegungdaten,
  Vorbelegungdaten,
  VwgVorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import {
  SpartenType,
  VertragsdatenRequestModel,
  VertragsVorbelegungdaten,
  VwgVorvertragsdaten,
} from '@ps20/bff-models';
import { AxiosError } from 'axios';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { equalAdressen } from '@ps20-frontend/vorschlagsdaten-lib';
import { IAppState } from '../appState';
import { VwgActions } from '../vwgActions';

export enum VorbelegungVwgRequestEventsEnum {
  VORBELEGUNG_VWG_REQUEST_SUCCESSFULL = 'VORBELEGUNG_VWG_REQUEST_SUCCESSFULL',
  VORBELEGUNG_VWG_REQUEST_FAILED = 'VORBELEGUNG_VWG_REQUEST_FAILED',
}

export function* watchVorbelegungRequested(): SagaIterator {
  yield takeEvery(GlobalActions.VORBELEGUNG_REQUESTED, safeSaga(vorbelegungSaga, App.VWG_V2));
  yield takeEvery(
    VorbelegungVwgRequestEventsEnum.VORBELEGUNG_VWG_REQUEST_SUCCESSFULL,
    safeSaga(successCallback, App.VWG_V2),
  );
  yield takeEvery(
    VorbelegungVwgRequestEventsEnum.VORBELEGUNG_VWG_REQUEST_FAILED,
    safeSaga(errorCallback, App.VWG_V2),
  );
  yield takeEvery(GlobalActions.VORBELEGUNG_FINISHED, safeSaga(zusatzoptionenVorbelegung, App.VWG_V2));
}

const globalActions = new GlobalActions();

export function* vorbelegungSaga(action: GlobalAction<VorbelegungPayload>): SagaIterator {
  const state: IAppState = yield select();
  if (action.payload.appId !== App.VWG_V2 || action.payload.angebotsId !== state.angebotsId) {
    return;
  }

  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* vwgDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten, action.payload.angebotsId);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      globalPut.put,
      globalActions.doRequest({
        appId: App.VWG_V2,
        angebotsId: action.payload.angebotsId,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungVwgRequestEventsEnum.VORBELEGUNG_VWG_REQUEST_SUCCESSFULL,
        errorEvent: VorbelegungVwgRequestEventsEnum.VORBELEGUNG_VWG_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<VwgVorvertragsdaten>): SagaIterator {
  const state: IAppState = yield select();

  if (state.angebotsId !== successAction.doRequestAction.payload.angebotsId) {
    return yield cancel();
  }

  const vwgVorbelegungdaten: VwgVorbelegungdaten = getVWGVorbelegungdaten(successAction.payload);
  yield* vwgDatenVorbelegen(vwgVorbelegungdaten, successAction.doRequestAction.payload.angebotsId);
}

const vwgActions = new VwgActions();

function* vwgDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten, angebotsId: string): SagaIterator {
  const state: IAppState = yield select();
  let beibehalten = false;
  const globalPut = GlobalPut.getInstance();
  const vwgVorbelegungdaten: VwgVorbelegungdaten = vorbelegungdaten as VwgVorbelegungdaten;
  if (!state.produkt?.produktlinien) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }
  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.VWG_V2,
        state.angebotsId,
      ),
    );
  }
  if (state.vertragsoptionen?.beibehalten) {
    vwgVorbelegungdaten.angebotsId = state.angebotsId;
    yield call(
      [globalPut, globalPut.put],
      globalActions.vorbelegungForBeibehaltungRequested(vwgVorbelegungdaten),
    );
    beibehalten = true;
  }

  yield call(
    globalPut.put,
    globalActions.vorbelegungFinished({
      appId: App.VWG_V2,
      vorhandeneVorbelegungdaten: vwgVorbelegungdaten,
      angebotsId,
      beibehalten,
    }),
  );
}

export function* errorCallback(error: DoRequestResponse<AxiosError>): SagaIterator {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  if (state.angebotsId !== error.doRequestAction.payload.angebotsId) {
    return yield cancel();
  }
  yield call(
    globalPut.put,
    globalActions.vorbelegungFinished({
      appId: App.VWG_V2,
      angebotsId: state.angebotsId,
    }),
  );
  yield cancel();
}

export function* zusatzoptionenVorbelegung(action: GlobalAction<VorbelegungPayload>): SagaIterator {
  const state: IAppState = yield select();
  if (action.payload.appId !== App.VWG_V2 || action.payload.angebotsId !== state.angebotsId) {
    return;
  }
  if (!action.payload.beibehalten) {
    let selectedRisikoort: BasisAdresse;

    if (
      state.vorbelegungen.adresseImmobilieVorbelegt === false ||
      !isBasisAdresseExist(state.risikoort as BasisAdresse)
    ) {
      const immobilieAction: GlobalAction<ImmobilieUebernehmenPayload> = yield take(
        GlobalActions.IMMOBILIE_UEBERNEHMEN,
      );
      selectedRisikoort = immobilieAction.payload.immobilie.risikoort as BasisAdresse;
    } else {
      selectedRisikoort = state.risikoort as BasisAdresse;
    }

    const vwgVorbelegungdaten: VwgVorbelegungdaten =
      action.payload.vorhandeneVorbelegungdaten as VwgVorbelegungdaten;

    if (equalAdressen(selectedRisikoort, vwgVorbelegungdaten.risikoort as BasisAdresse)) {
      yield put(vwgActions.produktZusatzoptionenVorbelegt(vwgVorbelegungdaten));
    }
  }
}

function getVWGVorbelegungdaten(
  bffVorbelegungdaten: VertragsVorbelegungdaten,
): VwgVorbelegungdaten {
  return mapVorbelegungdaten(SpartenType.WOHNGEBAEUDE_V2, bffVorbelegungdaten) as VwgVorbelegungdaten;
}
